/*
 * <greeting-video> custom element
 * Custom message field for the creator greeting-video product.
 *
 * - Limits the message to a base length (e.g. 100 chars) for variant 1.
 * - When the base limit is reached a popup offers to extend the message
 *   (e.g. up to 500 chars) which selects the upgrade variant (variant 2)
 *   so the native theme price update kicks in (Aufpreis).
 * - Stays in sync with the normal variant picker (selecting the long
 *   variant manually also unlocks the longer message and vice versa).
 */
if (!customElements.get('greeting-video')) {
  customElements.define(
    'greeting-video',
    class GreetingVideo extends HTMLElement {
      constructor() {
        super();
        this.dismissed = false;
      }

      connectedCallback() {
        this.textarea = this.querySelector('[data-gv-textarea]');
        this.counter = this.querySelector('[data-gv-count]');
        this.maxLabel = this.querySelector('[data-gv-max]');
        this.popup = this.querySelector('[data-gv-popup]');
        this.upgradeBtn = this.querySelector('[data-gv-upgrade]');
        this.dismissBtn = this.querySelector('[data-gv-dismiss]');
        this.info = this.querySelector('[data-gv-info]');

        this.baseMax = parseInt(this.getAttribute('data-base-max'), 10) || 100;
        this.extMax = parseInt(this.getAttribute('data-extended-max'), 10) || 500;
        this.baseValue = (this.getAttribute('data-base-value') || '').trim();
        this.extValue = (this.getAttribute('data-extended-value') || '').trim();

        this.productInfo = this.closest('product-info');

        if (this.textarea) {
          this.textarea.addEventListener('input', this.onInput.bind(this));
        }
        if (this.upgradeBtn) {
          this.upgradeBtn.addEventListener('click', this.onUpgrade.bind(this));
        }
        if (this.dismissBtn) {
          this.dismissBtn.addEventListener('click', () => {
            this.dismissed = true;
            this.hidePopup();
          });
        }

        this.initTips();

        this._onVariant = this.onVariantChange.bind(this);
        document.addEventListener('variant:change', this._onVariant);

        // Fallback: react immediately when an option in the variant picker
        // changes, regardless of the theme's variant:change payload.
        this._onPickerChange = this.onPickerChange.bind(this);
        document.addEventListener('change', this._onPickerChange);

        this.syncFromCurrentVariant();
        this.updateCounter();
      }

      disconnectedCallback() {
        document.removeEventListener('variant:change', this._onVariant);
        document.removeEventListener('change', this._onPickerChange);
        if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
        if (this._onTipKey) document.removeEventListener('keydown', this._onTipKey);
      }

      initTips() {
        this.tips = Array.from(this.querySelectorAll('[data-gv-tip]'));
        if (!this.tips.length) return;

        this.tips.forEach((tip) => {
          const btn = tip.querySelector('[data-gv-tip-toggle]');
          const bubble = tip.querySelector('[data-gv-tip-bubble]');
          if (!btn || !bubble) return;

          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const willOpen = bubble.hidden;
            this.closeAllTips();
            if (willOpen) {
              bubble.hidden = false;
              btn.setAttribute('aria-expanded', 'true');
            }
          });

          bubble.addEventListener('click', (event) => event.stopPropagation());
        });

        this._onDocClick = () => this.closeAllTips();
        document.addEventListener('click', this._onDocClick);

        this._onTipKey = (event) => {
          if (event.key === 'Escape') this.closeAllTips();
        };
        document.addEventListener('keydown', this._onTipKey);
      }

      closeAllTips() {
        if (!this.tips) return;
        this.tips.forEach((tip) => {
          const btn = tip.querySelector('[data-gv-tip-toggle]');
          const bubble = tip.querySelector('[data-gv-tip-bubble]');
          if (bubble) bubble.hidden = true;
          if (btn) btn.setAttribute('aria-expanded', 'false');
        });
      }

      onPickerChange(event) {
        const target = event.target;
        if (!target) return;
        const vs = this.variantSelects;
        if (!vs || !vs.contains(target)) return;
        // The clicked radio / select already reflects the new selection here.
        this.syncFromCurrentVariant();
      }

      norm(value) {
        return (value || '').trim().toLowerCase();
      }

      get variantSelects() {
        const scope = this.productInfo || document;
        return scope.querySelector('variant-selects');
      }

      currentSelectedValues() {
        const vs = this.variantSelects;
        if (!vs) return [];
        const values = [];
        vs.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
          values.push(input.getAttribute('data-option-value') || input.value);
        });
        vs.querySelectorAll('select').forEach((select) => {
          if (select.selectedOptions && select.selectedOptions[0]) {
            values.push(select.selectedOptions[0].value);
          }
        });
        return values;
      }

      isExtendedValue(value) {
        const v = this.norm(value);
        if (!v) return false;
        const ext = this.norm(this.extValue);
        if (ext && (v === ext || v.indexOf(ext) !== -1 || ext.indexOf(v) !== -1)) return true;
        if (this.extMax && v.indexOf(String(this.extMax)) !== -1) return true;
        return false;
      }

      isBaseValue(value) {
        const v = this.norm(value);
        if (!v) return false;
        const base = this.norm(this.baseValue);
        if (base && (v === base || v.indexOf(base) !== -1 || base.indexOf(v) !== -1)) return true;
        if (this.baseMax && v.indexOf(String(this.baseMax)) !== -1 && !this.isExtendedValue(value)) return true;
        return false;
      }

      isExtendedFromValues(values) {
        if (!Array.isArray(values)) return false;
        return values.some((value) => this.isExtendedValue(value));
      }

      isExtendedFromVariant(variant) {
        if (!variant) return null;
        let values = [];
        if (Array.isArray(variant.options)) values = variant.options.slice();
        ['option1', 'option2', 'option3'].forEach((key) => {
          if (variant[key]) values.push(variant[key]);
        });
        if (this.isExtendedFromValues(values)) return true;
        if (variant.title && this.isExtendedValue(variant.title)) return true;
        if (values.some((value) => this.isBaseValue(value))) return false;
        if (variant.title && this.isBaseValue(variant.title)) return false;
        return null;
      }

      get isExtended() {
        const values = this.currentSelectedValues();
        if (this.isExtendedFromValues(values)) return true;
        if (values.some((value) => this.isBaseValue(value))) return false;

        // Values match neither setting: fall back to the checked radio's
        // position (first = base, any later one = extended).
        const vs = this.variantSelects;
        if (vs) {
          const radios = Array.from(vs.querySelectorAll('input[type="radio"]'));
          const checkedIndex = radios.findIndex((radio) => radio.checked);
          if (checkedIndex >= 0) return checkedIndex > 0;
        }
        return false;
      }

      syncFromCurrentVariant() {
        const extended = this.isExtended;
        this.applyMax(extended ? this.extMax : this.baseMax, extended);
      }

      onVariantChange(event) {
        if (this.productInfo && event.target && event.target.closest) {
          const pi = event.target.closest('product-info');
          if (pi && pi !== this.productInfo) return;
        }

        const variant = event.detail && event.detail.variant;
        let extended = this.isExtendedFromVariant(variant);
        if (extended === null) extended = this.isExtended;

        this.applyMax(extended ? this.extMax : this.baseMax, extended);
        if (extended) {
          this.dismissed = false;
          this.hidePopup();
        }
      }

      applyMax(max, extended) {
        if (!this.textarea) return;
        this.textarea.setAttribute('maxlength', max);
        if (this.textarea.value.length > max) {
          this.textarea.value = this.textarea.value.slice(0, max);
        }
        if (this.maxLabel) this.maxLabel.textContent = max;
        if (this.info) {
          const baseInfo = this.info.getAttribute('data-base-info') || '';
          const extInfo = this.info.getAttribute('data-extended-info') || '';
          this.info.textContent = extended ? extInfo : baseInfo;
        }
        this.classList.toggle('is-extended', !!extended);
        this.updateCounter();
      }

      onInput() {
        this.updateCounter();
        const threshold = Math.ceil(this.baseMax * 0.8);
        if (
          !this.isExtended &&
          this.baseMax < this.extMax &&
          !this.dismissed &&
          this.textarea.value.length >= threshold
        ) {
          this.showPopup();
        }
      }

      updateCounter() {
        if (this.counter && this.textarea) {
          this.counter.textContent = this.textarea.value.length;
        }
      }

      showPopup() {
        if (!this.popup) return;
        this.popup.hidden = false;
        requestAnimationFrame(() => this.popup.classList.add('is-visible'));
      }

      hidePopup() {
        if (!this.popup) return;
        this.popup.classList.remove('is-visible');
        this.popup.hidden = true;
      }

      onUpgrade() {
        this.selectOptionValue(this.extValue);
        this.hidePopup();
        // Optimistic local update; the variant:change event confirms it.
        this.applyMax(this.extMax, true);
        if (this.textarea) this.textarea.focus();
      }

      selectOptionValue(value) {
        const vs = this.variantSelects;
        if (!vs || !value) return false;
        const target = this.norm(value);

        const radios = vs.querySelectorAll('input[type="radio"]');
        for (const radio of radios) {
          const radioValue = radio.getAttribute('data-option-value') || radio.value;
          if (this.norm(radioValue) === target) {
            if (!radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return true;
          }
        }

        const selects = vs.querySelectorAll('select');
        for (const select of selects) {
          const option = Array.from(select.options).find((opt) => this.norm(opt.value) === target);
          if (option) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            return true;
          }
        }

        return false;
      }
    }
  );
}
